import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Subscribers } from '../components/Subscribers/Subscribers';
import { getSubscribers } from '../api/podcasts';
import { Box, CircularProgress, Typography } from '@mui/material';

export const Admin = () => {
  const [allSubscribers, setAllSubscribers] = useState([]); // Store all data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all data once
  const handleFetchAllSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data without pagination
      const response = await getSubscribers({ limit: 10000 }); // Large limit to get all data
      
      // Handle both array response and object response with data/total
      if (Array.isArray(response)) {
        setAllSubscribers(response);
      } else {
        setAllSubscribers(response.data || response.subscribers || []);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err.message || 'Failed to fetch subscribers data');
      setAllSubscribers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Client-side filtering and sorting
  const filteredAndSortedSubscribers = useMemo(() => {
    let filtered = [...allSubscribers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(subscriber => {
        // Search in email
        const emailMatch = subscriber.email.toLowerCase().includes(query);
        
        // Search in podcast names
        const podcastMatch = subscriber.subscriptions.some(subscription => 
          subscription.podcast.name.toLowerCase().includes(query) ||
          subscription.podcast.artist.toLowerCase().includes(query)
        );
        
        return emailMatch || podcastMatch;
      });
    }

    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortBy) {
          case 'email':
            aValue = a.email.toLowerCase();
            bValue = b.email.toLowerCase();
            break;
          case 'totalSubscriptions':
            aValue = a.totalSubscriptions;
            bValue = b.totalSubscriptions;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [allSubscribers, searchQuery, sortBy, sortOrder]);

  // Client-side pagination
  const paginatedSubscribers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedSubscribers.slice(startIndex, endIndex);
  }, [filteredAndSortedSubscribers, page, rowsPerPage]);

  // Handle search - client-side only
  const handleSearch = useCallback((filters) => {
    setSearchQuery(filters.search || '');
    setPage(0); // Reset to first page when searching
  }, []);

  // Handle sorting - client-side only
  const handleSort = useCallback((column, order) => {
    setSortBy(column);
    setSortOrder(order);
    setPage(0); // Reset to first page when sorting
  }, []);

  // Handle pagination - client-side only
  const handlePageChange = useCallback((newPage, newRowsPerPage) => {
    if (newRowsPerPage !== undefined && newRowsPerPage !== rowsPerPage) {
      setRowsPerPage(newRowsPerPage);
      setPage(0); // Reset to first page when changing rows per page
    } else {
      setPage(newPage);
    }
  }, [rowsPerPage]);

  // Fetch data only once when component mounts
  useEffect(() => {
    handleFetchAllSubscribers();
  }, [handleFetchAllSubscribers]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="textSecondary">
          Loading subscribers...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h6" color="error">
          Error loading subscribers
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {error}
        </Typography>
        <button 
          onClick={handleFetchAllSubscribers}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </Box>
    );
  }

  return (
    <div className="admin-container">
      <Subscribers 
        subscribers={paginatedSubscribers}
        loading={false} // Never loading after initial fetch
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        totalCount={filteredAndSortedSubscribers.length}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
      />
    </div>
  );
}; 